import React, { useCallback, useEffect, useRef, useState } from "react";
import '../../components/Map/Location.scss';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  Pin,
  useMap,
  InfoWindow,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import dayjs from "dayjs";
import { getDeviceCategoryLabel } from "../../utils/deviceIcons";
import DeviceMapMarker from "./DeviceMapMarker";
import Battery from "../../assets/images/Battery.png";
import { Circle } from "../../components/components/circle";
import { GOOGLE_KEY } from "../../constants/Constant";
import DeviceStatusBadge from "../DeviceStatus/DeviceStatusBadge";

function DeviceAdvancedMarker({ poi, tripDetail, onMarkerClick, setMarkerRef }) {
  const showTripPin = Boolean(tripDetail?.length);

  const handleMarkerRef = useCallback((marker) => {
    setMarkerRef(marker, poi.key);
  }, [poi.key, setMarkerRef]);

  const handleClick = useCallback(() => {
    onMarkerClick(poi.key);
  }, [onMarkerClick, poi.key]);

  return (
    <AdvancedMarker
      position={poi.location}
      ref={handleMarkerRef}
      onClick={handleClick}
    >
      {showTripPin ? (
        <Pin
          background="red"
          glyphColor="#000"
          borderColor="#000"
        />
      ) : (
        <DeviceMapMarker category={poi.device?.deviceCategory} />
      )}
    </AdvancedMarker>
  );
}

function DeviceMarkers({ locationsList, tripDetail, onMarkerClick }) {
  const map = useMap();
  const [markers, setMarkers] = useState({});
  const clusterer = useRef(null);

  useEffect(() => {
    if (!map) return;
    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({
        map,
        onClusterClick: null,
      });
    }
  }, [map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(Object.values(markers));
  }, [markers]);

  const setMarkerRef = useCallback((marker, key) => {
    setMarkers((prev) => {
      if (marker) {
        if (prev[key] === marker) return prev;
        return { ...prev, [key]: marker };
      }
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return (
    <>
      {locationsList?.map((poi) => (
        <DeviceAdvancedMarker
          key={poi.key}
          poi={poi}
          tripDetail={tripDetail}
          onMarkerClick={onMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}
    </>
  );
}

function Location({ trip, zoneData, DevicesList, tripDetail, center, setCenter, selectedZone }) {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoom, setZoom] = useState(13)
  const [isState, setIsState] = useState(false);
  const [zoneLocation, setZoneLocation] = useState();
  const [zoneCenter, setZoneCenter] = useState();
  const [locationsList, setLocationsList] = useState([]);
  const [locationsZone, setLocationsZone] = useState([]);


  function handleCameraChange(ev) {
    if (isState) {
      setIsState(false)
      setZoneLocation(null)
    }
    setZoom(ev.detail.zoom)
    setCenter && setCenter(ev.detail.center)
  }

  const handleMarkerClick = useCallback((key) => {
    setSelectedMarker(key); // Set the selected marker key
  }, []);



  useEffect(() => {
    if (DevicesList) {
      setLocationsList(DevicesList?.map(device => {
        const { latitude, longitude } = device;
        const deviceKey = device.deviceId || device._id;
        return {
          key: deviceKey,
          location: {
            lat: parseFloat(latitude),
            lng: parseFloat(longitude)
          },
          device: device
        };
      }));
      console.log("Device update", DevicesList)
    }


  }, [DevicesList])
  useEffect(() => {
    if (zoneData) {
      setLocationsZone(zoneData?.map(zone => ({
        key: zone?._id,
        location: {
          lat: parseFloat(zone?.latitude),
          lng: parseFloat(zone?.longitude)
        },
        radius: zone?.radius * 1609.34,

        zone
      })));
      console.log("Zone update", zoneData)
    }

  }, [zoneData])

  const zoneRadius = zoneCenter?.map(zone => ({
    key: zone?._id,
    location: {
      lat: parseFloat(zone?.lat) || 0,
      lng: parseFloat(zone?.lng) || 0
    },
    radius: (zone?.miles || 0) * 1609.34,
  }));



  function Directions() {
    const map = useMap();
    const routesLibrary = useMapsLibrary('routes');
    const polylinesRef = useRef([]);

    useEffect(() => {
      if (!routesLibrary || !map || !tripDetail || tripDetail.length === 0) return;

      const Route = routesLibrary.Route;
      if (!Route?.computeRoutes) return;

      const origin = tripDetail[0].start_coordinate;
      const destination = tripDetail[tripDetail.length - 1].end_coordinate;
      const OLat = Number(origin?.latitude);
      const OLong = Number(origin?.longitude);
      const DLat = Number(destination?.latitude);
      const DLong = Number(destination?.longitude);

      if (!OLat || !OLong || !DLat || !DLong) return;

      const modifiedData = tripDetail.slice(1, tripDetail.length - 1);
      const maxWaypoints = 25;
      const intermediates = modifiedData.slice(0, maxWaypoints).map((data) => ({
        lat: Number(data?.latitude),
        lng: Number(data?.longitude),
      }));

      let cancelled = false;

      const clearPolylines = () => {
        polylinesRef.current.forEach((polyline) => polyline.setMap(null));
        polylinesRef.current = [];
      };

      (async () => {
        try {
          const { routes } = await Route.computeRoutes({
            origin: { lat: OLat, lng: OLong },
            destination: { lat: DLat, lng: DLong },
            intermediates,
            travelMode: 'DRIVING',
            computeAlternativeRoutes: true,
            fields: ['path'],
          });

          if (cancelled || !routes?.length) return;

          clearPolylines();

          const polylineOptions = {
            strokeColor: '#8d8986',
            strokeOpacity: 0.7,
            strokeWeight: 4,
            icons: [{
              icon: {
                path: window?.google?.maps?.SymbolPath?.CIRCLE,
                strokeOpacity: 0.5,
                scale: 1,
                strokeColor: 'white',
                strokeWeight: 4,
              },
              offset: '0',
              repeat: '20px',
            }],
          };

          const lines = routes[0].createPolylines();
          lines.forEach((polyline) => {
            polyline.setOptions(polylineOptions);
            polyline.setMap(map);
          });

          polylinesRef.current = lines;
        } catch (error) {
          console.error('Error fetching route:', error);
        }
      })();

      return () => {
        cancelled = true;
        clearPolylines();
      };
    }, [routesLibrary, map, tripDetail]);

    return null;
  }




  const [address, setAddress] = useState('');
  useEffect(() => {
    if (selectedMarker) {
      const location = locationsList?.find((poi) => poi.key === selectedMarker)?.location;
      getAddressFromLatLng(location?.lat, location?.lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarker]);

  const getAddressFromLatLng = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat, lng };

    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK") {
        if (results[0]) {
          const address = results[0].formatted_address;
          if (!address.includes("+")) {
            setAddress(address);
          } else {
            setAddress("Address not available");
          }
        } else {
          setAddress("Address not found");
        }
      } else {
        console.error("Geocode was not successful for the following reason: " + status);
        // setAddress("Error retrieving address");
      }
    });
  };

  useEffect(() => {

    if (selectedZone) {
      setZoneLocation({
        ...selectedZone,
        miles: isNaN(Number(selectedZone?.miles)) ? 5 : Number(selectedZone?.miles),
      });
      if (zoneLocation) {

        setZoneCenter([{
          ...selectedZone,
          miles: isNaN(Number(selectedZone?.miles)) ? 5 : Number(selectedZone?.miles),
        }]);
      }
      setIsState(true);
    }
  }, [selectedZone]);

  // console.log("zoneLocation", zoneLocation,"center",center);
  return (
    <APIProvider apiKey={GOOGLE_KEY}>
      <Map
        defaultZoom={13}
        defaultCenter={{ lat: 33.5779, lng: -101.8552 }}
        zoom={zoom}
        // center={ selectedZone?.lat && selectedZone?.lng
        //   ? { lat: parseFloat(selectedZone.lat), lng: parseFloat(selectedZone.lng) }
        //   : center}
        center={zoneLocation?.lat && zoneLocation?.lng
          ? { lat: parseFloat(zoneLocation.lat), lng: parseFloat(zoneLocation.lng) }
          : center}

        onCameraChanged={handleCameraChange}
        mapId="LOCSHARK_MAP_ID"
        fullscreenControl={false}
      >
        {trip && <Directions />}
        {locationsZone?.map((zone) => (
          <Circle
            key={zone.key}
            radius={zone.radiusInMiles ?? zone.radius / 1609.344}
            center={zone.location}
            strokeColor={'#0c4cb3'}
            strokeOpacity={1}
            strokeWeight={2}
            fillColor={'#3b82f6'}
            fillOpacity={0.1}
          // editable
          // draggable
          />
        )
        )}

        {zoneRadius?.map((zone) => (
          <Circle
            key={zone.key}
            radius={zone.radius}
            center={zone.location}
            strokeColor={'#0c4cb3'}
            strokeOpacity={1}
            strokeWeight={2}
            fillColor={'#3b82f6'}
            fillOpacity={0.2}
          // editable
          // draggable
          />
        ))}

        <DeviceMarkers
          locationsList={locationsList}
          tripDetail={tripDetail}
          onMarkerClick={handleMarkerClick}
        />

        {/* InfoWindow for selected marker */}
        {selectedMarker && (

          <InfoWindow
            position={locationsList?.find((poi) => poi?.key === selectedMarker)?.location}
            onCloseClick={() => setSelectedMarker(null)} // Close InfoWindow
            maxWidth={350}
          >
            <div className="location-tooltip-block">
              <div className="location-tooltip-head">
                <h4 className="location-tooltip-title">{locationsList?.find((poi) => poi?.key === selectedMarker)?.device?.deviceName}</h4>
                <DeviceStatusBadge device={locationsList?.find((poi) => poi?.key === selectedMarker)?.device} />
              </div>
              <div className="location-tooltip-type">
                Type: {getDeviceCategoryLabel(locationsList?.find((poi) => poi?.key === selectedMarker)?.device?.deviceCategory)}
              </div>
              <div className="date-time-text">
                {locationsList?.find((poi) => poi?.key === selectedMarker)?.device?.dateTime ? dayjs(locationsList?.find((poi) => poi?.key === selectedMarker)?.device?.dateTime).format('DD MMM YYYY HH:mm:ss') : "-"}
              </div>
              <div className="locattion-address">
                <div>{address}</div>
                <div className="battery-status">{locationsList?.find((poi) => poi?.key === selectedMarker)?.device?.batteryLevel}% <img
                  src={Battery}
                  alt="Custom Device Logo"
                  style={{
                    width: '30px',
                    height: '30px'
                  }}
                  data-tip
                /> </div>
              </div>
            </div>
          </InfoWindow>
        )}
      </Map>
    </APIProvider>
  );
}

export default React.memo(Location);
